import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useChatCandidates } from "../../backend/queries/users";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { cn } from "../../lib/utils";

type PeoplePickerProps = {
  /** Candidates are only fetched while this is true. */
  enabled: boolean;
  selected: string[];
  onToggle: (id: string) => void;
  /** People already in the conversation, left out of the list. */
  excludeIds?: string[];
  /** Replaces the "no people" text when there is nothing to choose from. */
  emptyText?: string;
  /** Makes the search field's id unique when two pickers could exist at once. */
  idPrefix?: string;
};

const SEARCH_DEBOUNCE_MS = 250;

/**
 * The search box and checklist staff use to choose people for a conversation.
 *
 * Candidates come from the assignment-options endpoint, which is already
 * scoped in Rust: an Admin sees every enabled account, an Agent sees only
 * themselves and clients on their own projects. That is exactly the set of
 * people staff should be able to message, so no new endpoint is needed.
 * The read lives in `useChatCandidates`. This component keeps the search box,
 * the rendering and nothing else.
 */
export function PeoplePicker({
  enabled,
  selected,
  onToggle,
  excludeIds = [],
  emptyText = "No people are available to message.",
  idPrefix = "chat",
}: PeoplePickerProps) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const candidates = useChatCandidates(debounced, { enabled });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = candidates;

  const people = useMemo(() => {
    const seen = new Set<string>();
    return (candidates.data?.pages ?? [])
      .flatMap((page) => page.items)
      .filter((person) => {
        if (excludeIds.includes(person.id) || seen.has(person.id)) return false;
        seen.add(person.id);
        return true;
      });
  }, [candidates.data, excludeIds]);

  // A page of people who are all in the group already must not read as
  // "everyone is included" while eligible people wait on the next page.
  useEffect(() => {
    if (!people.length && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [people.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const searching = !people.length && (hasNextPage || isFetchingNextPage);

  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-people-search`}>Search people</Label>
        <Input
          id={`${idPrefix}-people-search`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Name"
          autoComplete="off"
        />
      </div>

      <div className="max-h-56 overflow-y-auto rounded-md border">
        {candidates.isPending || searching ? (
          <p className="flex items-center gap-2 px-3 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading people
          </p>
        ) : candidates.error ? (
          <div className="space-y-2 px-3 py-6 text-center">
            <p className="text-sm text-muted-foreground">
              People could not be loaded.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="min-h-touch md:min-h-0"
              onClick={() => void candidates.refetch()}
            >
              Try again
            </Button>
          </div>
        ) : !people.length ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            {debounced ? "Nobody matches that search." : emptyText}
          </p>
        ) : (
          <>
            <ul className="divide-y">
              {people.map((person) => {
                const checked = selected.includes(person.id);
                const name =
                  [person.firstname, person.lastname].filter(Boolean).join(" ") ||
                  "Portal user";
                return (
                  <li key={person.id}>
                    <button
                      type="button"
                      onClick={() => onToggle(person.id)}
                      aria-pressed={checked}
                      className={cn(
                        "flex min-h-touch w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm md:min-h-0",
                        "hover:bg-accent hover:text-accent-foreground",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                        checked && "bg-accent text-accent-foreground",
                      )}
                    >
                      <span className="truncate">{name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {person.role}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {hasNextPage ? (
              <div className="flex justify-center border-t p-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="min-h-touch md:min-h-0"
                  disabled={isFetchingNextPage}
                  onClick={() => void fetchNextPage()}
                >
                  {isFetchingNextPage ? "Loading more people" : "Show more people"}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}
